import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';

import EmailConditionModal from './EmailConditionModal';
import {
  Mail,
  Plus,
  Trash2,
  Eye
} from 'lucide-react';

const EmailNotificationConditions = ({
  campaignId,
  activeForm,
  formFields,
  api,
  setSuccessMessage,
  setShowSuccess,
  campaign // Add campaign prop
}) => {
  const [emailConditions, setEmailConditions] = useState([]);
  const [showEmailConditionModal, setShowEmailConditionModal] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [loadingConditions, setLoadingConditions] = useState(false);

  useEffect(() => {
    if (activeForm?.id) {
      loadEmailConditions();
    } else {
      setEmailConditions([]);
    }
  }, [activeForm?.id]);

  const loadEmailConditions = async () => {
    if (!activeForm?.id) return;

    try {
      setLoadingConditions(true);
      const response = await api.get(`/admin/campaigns/${campaignId}/email-conditions?form_id=${activeForm.id}`);
      setEmailConditions(response.data.conditions || []);
    } catch (err) {
      console.error('Failed to load email conditions:', err);
      setEmailConditions([]);
    } finally {
      setLoadingConditions(false);
    }
  };

  const handleCreateEmailCondition = () => {
    setEditingCondition(null);
    setShowEmailConditionModal(true);
  };

  const handleEditEmailCondition = (condition) => {
    setEditingCondition(condition);
    setShowEmailConditionModal(true);
  };

  const handleDeleteEmailCondition = async (conditionId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete this email notification condition.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/campaigns/${campaignId}/email-conditions/${conditionId}`);
        await loadEmailConditions();
        if (setSuccessMessage) {
          setSuccessMessage('Email notification condition deleted successfully!');
        }
        if (setShowSuccess) {
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
        }
      } catch (err) {
        console.error('Failed to delete email condition:', err);
        await Swal.fire('Error', 'Failed to delete email condition', 'error');
      }
    }
  };

  const handleModalClose = () => {
    setShowEmailConditionModal(false);
    setEditingCondition(null);
  };

  const handleConditionSaved = async () => {
    await loadEmailConditions();
    handleModalClose();
    if (setSuccessMessage) {
      setSuccessMessage('Email notification condition saved successfully!');
    }
    if (setShowSuccess) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-900">Conditional Notifications</h4>
              <p className="text-sm text-gray-600">
                Manage email rules that trigger when specific form responses are submitted.
              </p>
            </div>
          </div>
          <button
            onClick={handleCreateEmailCondition}
            disabled={!activeForm}
            className={`px-4 py-2 text-white rounded-lg text-sm flex items-center space-x-1 ${
              activeForm 
                ? 'bg-teal-600 hover:bg-teal-700' 
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            <Plus className="h-4 w-4" />
            <span>Add Condition</span>
          </button>
        </div>

        {!activeForm ? (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Link a form to this campaign before creating email notification conditions.
            </p>
          </div>
        ) : loadingConditions ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg text-gray-500 text-sm">
            Loading email notifications...
          </div>
        ) : emailConditions.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <Mail className="mx-auto h-8 w-8 text-gray-400 mb-2" />
            <p className="text-gray-600">No conditional notifications set up yet.</p>
            <p className="text-sm text-gray-500 mt-1">Click "Add Condition" to create your first notification rule.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {emailConditions.map((condition) => (
              <div key={condition.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h5 className="font-medium text-gray-900">{condition.condition_name}</h5>
                      {condition.is_enabled ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Enabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Disabled
                        </span>
                      )}
                    </div>
                    {condition.condition_description && (
                      <p className="text-sm text-gray-600 mb-2">{condition.condition_description}</p>
                    )}
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>
                        <strong>Condition:</strong>{' '}
                        {condition.condition_config?.field_name} {condition.condition_config?.operator}{' '}
                        "{condition.condition_config?.value}"
                      </p>
                      {(condition.from_name || condition.from_email_address) && (
                        <p>
                          <strong>Sender:</strong>{' '}
                          {condition.from_name ? `${condition.from_name} ` : ''}
                          {condition.from_email_address ? `<${condition.from_email_address}>` : ''}
                        </p>
                      )}
                      <p><strong>Subject:</strong> {condition.email_subject}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEditEmailCondition(condition)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
                      title="View or edit condition"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteEmailCondition(condition.id)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg"
                      title="Delete condition"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showEmailConditionModal && (
        <EmailConditionModal
          campaignId={campaignId}
          formId={activeForm?.id}
          formFields={formFields}
          condition={editingCondition}
          onClose={handleModalClose}
          onSave={handleConditionSaved}
          api={api}
          campaign={campaign}
        />
      )}
    </>
  );
};

export default EmailNotificationConditions;
